import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, ArrowUpRight, Globe } from 'lucide-react';
import ParticleField from './ParticleField';
import FloatingLogo from './FloatingLogo';
import { brand, nav } from '../mock';

// Reveal-on-scroll wrapper (subtle entrance animation)
export function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          io.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`reveal ${inView ? 'in-view' : ''} ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="relative overflow-hidden bg-[#0f2226] text-[#d5ddda]">
      <ParticleField density={1.1} variant="dark" />
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-12 z-[5] bg-gradient-to-b from-[#faf9f5] to-transparent" />
      <div className="absolute inset-0 z-[1] hidden md:block pointer-events-none">
        <FloatingLogo src={brand.logoTransparent} size={170} speed={0.45} vary />
      </div>
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 sm:px-10 lg:px-16 py-16 pt-20">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <img
              src={brand.logoTransparent}
              alt="Herbert Project Partners"
              className="w-56 max-w-full drop-shadow-[0_14px_45px_rgba(0,0,0,0.5)] md:hidden"
            />
            <div className="mt-6 flex flex-col gap-2 text-[13px] text-[#9fb0ac] md:mt-0">
              <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-[#c6a94b]" /> {brand.legal.location}</span>
              <a href="https://herbertprojectpartners.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-white transition-colors">
                <Globe className="h-4 w-4 text-[#c6a94b]" /> herbertprojectpartners.com
              </a>
              <a href="mailto:hello@herbertprojectpartners.com" className="inline-flex items-center gap-2 hover:text-white transition-colors">
                <Mail className="h-4 w-4 text-[#c6a94b]" /> hello@herbertprojectpartners.com
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="eyebrow text-[#c6a94b]">Navigate</div>
            <ul className="mt-5 space-y-3">
              {nav.map((n) => (
                <li key={n.to}>
                  <Link to={n.to} className="group inline-flex items-center gap-1 text-[14px] text-[#c3cfcb] hover:text-white transition-colors">
                    {n.label}
                    <ArrowUpRight className="h-3.5 w-3.5 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="eyebrow text-[#c6a94b]">Trust &amp; Governance</div>
            <ul className="mt-5 space-y-3 text-[14px]">
              <li><a href="#privacy" className="text-[#c3cfcb] hover:text-white transition-colors">Privacy policy</a></li>
              <li><a href="#terms" className="text-[#c3cfcb] hover:text-white transition-colors">Website terms</a></li>
              <li><Link to="/about#commitments" className="text-[#c3cfcb] hover:text-white transition-colors">Security &amp; Responsible-AI</Link></li>
            </ul>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-7 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <p className="text-[12.5px] text-[#8ea09b]">
            {brand.legal.copyright}. {brand.legal.rights}
          </p>
          <p className="text-[12.5px] text-[#8ea09b]">
            {brand.legal.entity} {brand.legal.abn}
          </p>
        </div>
        <p className="mt-4 text-[11.5px] leading-relaxed text-[#6f817d] max-w-3xl">
          Advisory content on this website is provided for general information and does not constitute professional, legal or financial advice. EIOM is proprietary intellectual property of Herbert Project Partners.
        </p>
        <p className="mt-3 text-[11.5px] leading-relaxed text-[#6f817d] max-w-3xl">
          Herbert Project Partners Insights synthesises and interprets source material; it does not republish third-party content. Primary sources are preferred, all material claims are attributable, and third-party visuals or substantial extracts are used only where licensed, permitted or expressly authorised.
        </p>
      </div>
    </footer>
  );
}
